import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { analytics } from '@/lib/analytics';
import type { Organization } from '@/types/core';
import { useTimeFilterStore } from './timeFilterStore';

interface OrgState {
  currentOrg: Organization | null;
  organizations: Organization[];
  setCurrentOrg: (org: Organization) => void;
  setOrganizations: (orgs: Organization[]) => void;
  /**
   * Clear currentOrg. When `autoSelectAlternate` is true, pick the next
   * available org (excluding the one we're clearing). Used by the 403
   * interceptor when the user loses access to their current org so they
   * always land on a valid one without a dead UI.
   */
  clearCurrentOrg: (opts?: { autoSelectAlternate?: boolean }) => void;
  /** The real org displaced by demo mode, restored on exit. Never persisted. */
  preDemoOrg: Organization | null;
  /**
   * Drop all org state (current org AND the list). Called when the session
   * ends: an org is tenant data that belongs to a session, and one left behind
   * makes public pages that read `currentOrg` issue authenticated requests.
   */
  reset: () => void;
  /**
   * Put the synthetic demo org in scope for the public `/demo` route.
   *
   * Deliberately NOT `setCurrentOrg`: that fires `analytics.updateUser`, which
   * would tag every anonymous demo visitor with a fictional org id and pollute
   * the funnel we use to measure whether the demo converts at all.
   *
   * The previous real org is stashed and restored on exit, so a signed-in user
   * who wanders into `/demo` gets their own org back rather than being dumped
   * into an org-less state.
   */
  enterDemoOrg: (org: Organization) => void;
  /** Leave demo mode, restoring whatever org was in scope beforehand. */
  exitDemoOrg: () => void;
}

/**
 * The synthetic org's id. Declared here rather than imported from
 * `lib/demo/fixture` so the store's persistence guard has no dependency on the
 * demo module - the guard must hold even if the demo bundle never loads.
 */
const DEMO_ORG_ID = 'demo-org';

/** True for the synthetic demo org, which must never be persisted. */
export const isDemoOrg = (org: Organization | null | undefined): boolean =>
  org?.id === DEMO_ORG_ID;

export const useOrgStore = create<OrgState>()(
  persist(
    (set, get) => ({
      currentOrg: null,
      organizations: [],

      setCurrentOrg: (org: Organization) => {
        // Clear repo and team filters when switching orgs (they are org-specific)
        useTimeFilterStore.getState().setSelectedRepoId(null);
        useTimeFilterStore.getState().setSelectedTeamId(null);

        set({ currentOrg: org });

        // Update analytics with new org context
        analytics.updateUser({
          orgId: org.id,
          orgSlug: org.slug,
          orgName: org.name,
          isPersonal: org.is_personal,
        });
      },

      setOrganizations: (orgs: Organization[]) => {
        set((state) => {
          // Reconcile currentOrg with the new orgs list:
          // - if currentOrg still exists, adopt its FRESH copy from the list so
          //   org-level settings (e.g. individual_metrics) are never served from a
          //   stale persisted snapshot in localStorage
          // - if currentOrg is missing or was removed, fall back to the first org
          const nextCurrent =
            orgs.find((o) => o.id === state.currentOrg?.id) ?? orgs[0] ?? null;
          return { organizations: orgs, currentOrg: nextCurrent };
        });
      },

      clearCurrentOrg: (opts) => {
        useTimeFilterStore.getState().setSelectedRepoId(null);
        useTimeFilterStore.getState().setSelectedTeamId(null);
        const { currentOrg, organizations } = get();
        const failedId = currentOrg?.id;

        if (opts?.autoSelectAlternate) {
          const alternate = organizations.find((o) => o.id !== failedId);
          if (alternate) {
            set({ currentOrg: alternate });
            analytics.updateUser({
              orgId: alternate.id,
              orgSlug: alternate.slug,
              orgName: alternate.name,
              isPersonal: alternate.is_personal,
            });
            return;
          }
        }
        set({ currentOrg: null });
      },

      reset: () => {
        useTimeFilterStore.getState().setSelectedRepoId(null);
        useTimeFilterStore.getState().setSelectedTeamId(null);
        set({ currentOrg: null, organizations: [], preDemoOrg: null });
      },

      preDemoOrg: null,

      enterDemoOrg: (org: Organization) => {
        const { currentOrg } = get();
        if (isDemoOrg(currentOrg)) return; // idempotent - seeded during render

        // Scope filters are org-specific, and a repo id from the visitor's real
        // org would select nothing in the demo (leaving an empty dashboard that
        // looks like the product found no data).
        useTimeFilterStore.getState().setSelectedRepoId(null);
        useTimeFilterStore.getState().setSelectedTeamId(null);

        set({ preDemoOrg: currentOrg, currentOrg: org });
      },

      exitDemoOrg: () => {
        const { currentOrg, preDemoOrg } = get();
        if (!isDemoOrg(currentOrg)) return;

        useTimeFilterStore.getState().setSelectedRepoId(null);
        useTimeFilterStore.getState().setSelectedTeamId(null);

        set({ currentOrg: preDemoOrg, preDemoOrg: null });
      },
    }),
    {
      name: 'org-storage',
      // Never persist the synthetic demo org.
      //
      // It is not tenant data - no such org exists server-side. If it survived
      // into localStorage, a visitor who browsed /demo and then signed in would
      // restore `currentOrg = demo-org` and every page would issue real,
      // authenticated requests against an org id the backend has never heard
      // of. `preDemoOrg` is dropped for the same reason: it is session-scoped
      // scaffolding, not state worth restoring days later.
      partialize: (state) => ({
        organizations: state.organizations,
        currentOrg: isDemoOrg(state.currentOrg) ? state.preDemoOrg : state.currentOrg,
      }),
      // Never restore an org without a session. Logout used to leave this
      // snapshot behind, and every public page that reads `currentOrg`
      // (/pricing via usePermissions, the /demo app shell) then fired
      // authenticated requests whose 401 hard-redirected an anonymous visitor
      // to /login. Guarding here also heals browsers already in that state and
      // covers the interceptor paths that drop tokens and reload.
      merge: (persisted, current) =>
        localStorage.getItem('access_token')
          ? { ...current, ...(persisted as Partial<OrgState>) }
          : current,
    }
  )
);
